import { useUserInitialization } from '@readsavor/integration';
import { useAuthStore, userQueryKeys } from '@readsavor/auth';
import { queryClient } from '@/providers/AppServiceProviders';
import React, { useEffect, useRef } from 'react';

/**
 * This component orchestrates the user initialization process after authentication.
 * It listens for the authenticated state and triggers the initialization mutation.
 * It is designed to be resilient to React Strict Mode's double invocation.
 * This component does not render anything.
 */
export const UserInitializationOrchestrator: React.FC = () => {
  const status = useAuthStore(state => state.status);
  const setInitializationComplete = useAuthStore(state => state.setInitializationComplete);
  const initializationTriggered = useRef(false);

  const { mutate: initializeUser, reset } = useUserInitialization({
    onMutate: () => {
      setInitializationComplete(false); // Set UI to "preparing" state
    },
    onSuccess: () => {},
    onError: error => {
      console.error('[UserInitializationOrchestrator] User initialization failed:', error);
    },
    onSettled: () => {
      setInitializationComplete(true); // Unlock UI regardless of outcome
      // Refetch all user-related queries to ensure data consistency
      queryClient.refetchQueries({ queryKey: userQueryKeys.all });
    },
  });

  useEffect(() => {
    // Trigger initialization only once when the user becomes authenticated.
    // The ref ensures this logic is impervious to React Strict Mode's double-render behavior.
    if (status === 'authenticated' && !initializationTriggered.current) {
      initializationTriggered.current = true;
      initializeUser();
    }
  }, [status, initializeUser]);

  // Effect to reset the initialization state upon logout.
  // This ensures that if a user logs out and logs back in, the initialization
  // process can be correctly re-triggered.
  useEffect(() => {
    if (status === 'unauthenticated') {
      initializationTriggered.current = false;
      setInitializationComplete(false); // Reset the initialization completion flag
      reset(); // Reset the mutation state from react-query
    }
  }, [status, reset, setInitializationComplete]);

  return null;
};
